import { useEffect, useRef } from 'react';
import { message } from 'antd';

import { isOrbitHistoryTurn, updateOrbitSession, useOrbitSessionRecord } from 'api/orbit';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from './i18n/TModules';
import { setOrbitPendingWorkspace, useOrbitPendingWorkspaces } from './orbitPendingWorkspace';
import { findOrbitWorkspace } from './orbitWorkspaces';
import { OrbitTurn } from './types';

/**
 * Files the open chat in the Workspace chosen for it once an answer lists it: the server refuses a
 * PATCH (404) before then. Leaving the chat first drops the choice, so it stays in Recent.
 */
const useOrbitWorkspaceFiling = (
  sessionId: string | null,
  turns: OrbitTurn[],
  activeTurn: OrbitTurn | undefined
): void => {
  const { t } = useMultiLanguageTranslation();
  const pending = useOrbitPendingWorkspaces();
  const record = useOrbitSessionRecord(sessionId);
  const filing = useRef<string | null>(null);
  const previousSessionId = useRef(sessionId);
  const tag = sessionId === null ? undefined : pending[sessionId];
  const listable =
    record !== undefined ||
    turns.some(isOrbitHistoryTurn) ||
    (activeTurn?.session_id === sessionId && isOrbitHistoryTurn(activeTurn));

  useEffect(() => {
    const previous = previousSessionId.current;
    previousSessionId.current = sessionId;

    if (previous !== null && previous !== sessionId) {
      setOrbitPendingWorkspace(previous, null);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId === null || tag === undefined || !listable || filing.current === sessionId) {
      return;
    }

    filing.current = sessionId;
    updateOrbitSession(sessionId, { workspace: tag }).then(outcome => {
      filing.current = null;
      // Filed, gone or refused: from here on the server's value is the chat's Workspace.
      setOrbitPendingWorkspace(sessionId, null);

      if (outcome.kind !== 'ok') {
        message.error(
          t(`${TOrbit}.page.chatUpdate.fileFailed`, { name: findOrbitWorkspace(tag)?.title ?? '' })
        );
      }
    });
  }, [sessionId, tag, listable, t]);
};

export default useOrbitWorkspaceFiling;
